import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useCelebration } from '@/context/CelebrationContext';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  ArrowLeft, Users, MessageSquare, Plus, Heart, Send, 
  Bookmark, BookmarkCheck, ChevronDown, ChevronUp, Image as ImageIcon,
  X, MoreHorizontal, Mail, Clock, Flag, Mic
} from 'lucide-react';
import { ReportButton } from '@/components/ReportDialog';
import { VoiceInput } from '@/components/voice/VoiceInput';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Reaction icons and labels
const REACTIONS = {
  support: { icon: '❤️', label: 'Support' },
  praying: { icon: '🙏', label: 'Praying' },
  celebrate: { icon: '👏', label: 'Celebrate' },
  insightful: { icon: '💎', label: 'Insightful' }
};

// Support type prompts
const SUPPORT_TYPES = [
  { value: 'perspective', label: 'Perspective', description: 'Looking for different viewpoints' },
  { value: 'accountability', label: 'Accountability', description: 'Need someone to help me follow through' },
  { value: 'prayer', label: 'Prayer', description: 'Seeking spiritual support' },
  { value: 'feedback', label: 'Feedback', description: 'Want honest input on something' }
];

// Format relative time
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInSeconds = Math.floor((now - date) / 1000);
  
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString();
};

// Comment Component (supports threading)
const CommentThread = ({ comment, onReply, onSendDM, depth = 0 }) => {
  const [showReplyForm, setShowReplyForm] = useState(false);
  const [replyContent, setReplyContent] = useState('');
  const [collapsed, setCollapsed] = useState(false);
  
  const maxDepth = 2; // Max 3 levels (0, 1, 2)
  
  const handleReply = () => {
    if (replyContent.trim()) {
      onReply(comment.id, replyContent);
      setReplyContent('');
      setShowReplyForm(false);
    }
  };
  
  return (
    <div className={`${depth > 0 ? 'ml-6 border-l-2 border-sand pl-4' : ''}`}>
      <div className="flex gap-3 py-3">
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarImage src={comment.author_image} />
          <AvatarFallback className="bg-terracotta/20 text-terracotta text-xs">
            {comment.author_name?.charAt(0)?.toUpperCase()}
          </AvatarFallback>
        </Avatar>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-medium text-forest text-sm">{comment.author_name}</span>
            <span className="text-xs text-gray-400">{formatTimeAgo(comment.created_at)}</span>
          </div>
          <p className="text-gray-700 text-sm mt-1 whitespace-pre-wrap">{comment.content}</p>
          
          <div className="flex items-center gap-4 mt-2">
            {depth < maxDepth && (
              <button 
                onClick={() => setShowReplyForm(!showReplyForm)}
                className="text-xs text-forest hover:text-terracotta transition-colors"
              >
                Reply
              </button>
            )}
            <button 
              onClick={() => onSendDM(comment.author_id, comment.author_name)}
              className="text-xs text-gray-500 hover:text-forest transition-colors flex items-center gap-1"
            >
              <Mail className="h-3 w-3" /> Reply privately
            </button>
          </div>
          
          {showReplyForm && (
            <div className="mt-3 space-y-2">
              <div className="flex gap-2">
                <Input
                  value={replyContent}
                  onChange={(e) => setReplyContent(e.target.value)}
                  placeholder="Write a reply..."
                  className="text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && handleReply()}
                />
                <Button size="sm" onClick={handleReply} className="bg-forest hover:bg-forest/90">
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <VoiceInput 
                onTranscript={(text) => setReplyContent(prev => prev ? `${prev} ${text}` : text)}
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Nested replies */}
      {comment.replies && comment.replies.length > 0 && (
        <div className="mt-1">
          {!collapsed ? (
            <>
              {comment.replies.map((reply) => (
                <CommentThread 
                  key={reply.id} 
                  comment={reply} 
                  onReply={onReply} 
                  onSendDM={onSendDM}
                  depth={depth + 1} 
                />
              ))}
              {comment.replies.length > 2 && (
                <button 
                  onClick={() => setCollapsed(true)}
                  className="text-xs text-gray-500 hover:text-forest ml-6 py-1"
                >
                  Collapse replies
                </button>
              )}
            </>
          ) : (
            <button 
              onClick={() => setCollapsed(false)}
              className="text-xs text-forest hover:text-terracotta ml-6 py-1 flex items-center gap-1"
            >
              <ChevronDown className="h-3 w-3" />
              Show {comment.replies.length} {comment.replies.length === 1 ? 'reply' : 'replies'}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// Post Card Component
const PostCard = ({ post, community, onReact, onBookmark, onComment, onSendDM, isPrayerRoom }) => {
  const [expanded, setExpanded] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [showReactions, setShowReactions] = useState(false);
  
  const token = localStorage.getItem('token');
  const contentPreviewLength = 400;
  const needsExpand = post.content.length > contentPreviewLength;
  
  const fetchComments = async () => {
    if (loadingComments) return;
    setLoadingComments(true);
    try {
      const response = await axios.get(
        `${API_URL}/api/communities/${post.community_id}/posts/${post.id}/comments`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };
  
  const handleToggleComments = () => {
    if (!showComments && comments.length === 0) {
      fetchComments();
    }
    setShowComments(!showComments);
  };
  
  const handleAddComment = async (parentId = null, content = newComment) => {
    if (!content.trim()) return;
    
    try {
      await axios.post(
        `${API_URL}/api/communities/${post.community_id}/posts/${post.id}/comments`,
        { content, parent_comment_id: parentId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNewComment('');
      fetchComments();
      onComment();
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };
  
  return (
    <Card className="card-base overflow-hidden" data-testid={`post-card-${post.id}`}>
      <CardContent className="p-0">
        {/* Post Header */}
        <div className="p-4 pb-0">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={post.author_image} />
                <AvatarFallback className="bg-forest text-white">
                  {post.author_name?.charAt(0)?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-forest">{post.author_name}</span>
                  <span className="text-xs px-2 py-0.5 bg-sand/50 text-forest/70 rounded-full">
                    {community?.name?.split('|')[0]?.trim() || 'Community'}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <Clock className="h-3 w-3" />
                  {formatTimeAgo(post.created_at)}
                  {post.support_type && (
                    <span className="px-2 py-0.5 bg-terracotta/10 text-terracotta rounded-full">
                      Seeking {post.support_type}
                    </span>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              <button 
                onClick={() => onBookmark(post.id)}
                className="p-2 hover:bg-sand/50 rounded-lg transition-colors"
                data-testid={`bookmark-btn-${post.id}`}
              >
                {post.is_bookmarked ? (
                  <BookmarkCheck className="h-5 w-5 text-terracotta" />
                ) : (
                  <Bookmark className="h-5 w-5 text-gray-400 hover:text-forest" />
                )}
              </button>
              <ReportButton 
                contentType="post"
                reportedUserId={post.author_id}
                reportedContentId={post.id}
                reportedUserName={post.author_name}
              />
            </div>
          </div>
          
          {/* Post Title */}
          {post.title && (
            <h3 className="font-heading text-lg font-semibold text-forest mt-3">
              {post.title}
            </h3>
          )}
        </div>
        
        {/* Post Content */}
        <div className="px-4 py-3">
          <p className="text-gray-700 whitespace-pre-wrap leading-relaxed">
            {expanded || !needsExpand 
              ? post.content 
              : `${post.content.slice(0, contentPreviewLength)}...`}
          </p>
          {needsExpand && (
            <button 
              onClick={() => setExpanded(!expanded)}
              className="text-terracotta text-sm font-medium mt-2 hover:underline"
            >
              {expanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </div>
        
        {/* Post Media */}
        {post.media_urls && post.media_urls.length > 0 && (
          <div className="px-4 pb-3">
            <div className={`grid gap-2 ${post.media_urls.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {post.media_urls.map((url, idx) => (
                <div key={idx} className="rounded-lg overflow-hidden bg-sand/30">
                  {url.includes('.mp4') || url.includes('.mov') || url.includes('.webm') ? (
                    <video src={`${API_URL}${url}`} controls className="w-full max-h-80 object-contain" />
                  ) : (
                    <img 
                      src={url.startsWith('http') ? url : `${API_URL}${url}`} 
                      alt="Post media" 
                      className="w-full max-h-80 object-cover"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Reactions Bar */}
        {post.allow_reactions !== false && (
          <div className="px-4 py-2 border-t border-sand/50">
            <div className="flex items-center justify-between">
              {/* Reaction Counts */}
              <div className="flex items-center gap-1">
                {post.total_reactions > 0 && (
                  <div className="flex items-center gap-1 text-sm text-gray-500">
                    {Object.entries(post.reactions || {}).map(([type, count]) => (
                      count > 0 && (
                        <span key={type} className="flex items-center">
                          {REACTIONS[type]?.icon}
                        </span>
                      )
                    ))}
                    <span className="ml-1">{post.total_reactions}</span>
                  </div>
                )}
              </div>
              
              {/* Comment & Share counts */}
              <div className="flex items-center gap-3 text-sm text-gray-500">
                {post.comment_count > 0 && (
                  <button onClick={handleToggleComments} className="hover:text-forest">
                    {post.comment_count} {post.comment_count === 1 ? 'comment' : 'comments'}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Action Buttons */}
        <div className="px-4 py-2 border-t border-sand/50 flex items-center gap-2">
          {/* Reaction Picker */}
          <div className="relative">
            <button 
              onClick={() => setShowReactions(!showReactions)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                post.user_reactions?.length > 0 
                  ? 'bg-terracotta/10 text-terracotta' 
                  : 'hover:bg-sand/50 text-gray-600'
              }`}
              data-testid={`react-btn-${post.id}`}
            >
              {post.user_reactions?.length > 0 
                ? REACTIONS[post.user_reactions[0]]?.icon 
                : <Heart className="h-5 w-5" />
              }
              <span className="text-sm">{isPrayerRoom ? 'Praying' : 'React'}</span>
            </button>
            
            {showReactions && (
              <div 
                className="absolute bottom-full left-0 mb-2 flex gap-1 bg-white shadow-lg rounded-full p-2 border border-sand/50"
                onMouseLeave={() => setShowReactions(false)}
              >
                {Object.entries(REACTIONS).map(([type, { icon, label }]) => (
                  <button
                    key={type}
                    onClick={() => {
                      onReact(post.id, type);
                      setShowReactions(false);
                    }}
                    className={`p-2 hover:bg-sand/50 rounded-full transition-transform hover:scale-125 ${
                      post.user_reactions?.includes(type) ? 'bg-sand' : ''
                    }`}
                    title={label}
                  >
                    <span className="text-xl">{icon}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          
          {/* Comment Button */}
          <button 
            onClick={handleToggleComments}
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-sand/50 text-gray-600 transition-colors"
            data-testid={`comment-btn-${post.id}`}
          >
            <MessageSquare className="h-5 w-5" />
            <span className="text-sm">Reply</span>
          </button>
          
          {/* DM Button */}
          <button 
            onClick={() => onSendDM(post.author_id, post.author_name)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-sand/50 text-gray-600 transition-colors ml-auto"
          >
            <Mail className="h-4 w-4" />
            <span className="text-sm hidden sm:inline">Reply via DM</span>
          </button>
        </div>
        
        {/* Comments Section */}
        {showComments && (
          <div className="px-4 py-3 bg-cream/50 border-t border-sand/50">
            {/* Comment Tone Reminder */}
            <div className="text-xs text-gray-500 italic mb-3 flex items-center gap-2">
              <span className="w-1 h-4 bg-terracotta/30 rounded-full"></span>
              Respond with wisdom, care, and accountability.
            </div>
            
            {/* New Comment Input */}
            <div className="space-y-2 mb-4">
              <div className="flex gap-2">
                <Textarea
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Write a thoughtful response..."
                  className="resize-none text-sm"
                  rows={2}
                  data-testid="comment-textarea"
                />
                <Button 
                  onClick={() => handleAddComment()}
                  className="bg-forest hover:bg-forest/90 self-end"
                  disabled={!newComment.trim()}
                  data-testid="submit-comment-btn"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <VoiceInput 
                  onTranscript={(text) => setNewComment(prev => prev ? `${prev} ${text}` : text)}
                />
                <span className="text-xs text-gray-400">or record your response</span>
              </div>
            </div>
            
            {/* Comments List */}
            {loadingComments ? (
              <div className="text-center py-4 text-gray-500">Loading comments...</div>
            ) : comments.length > 0 ? (
              <div className={`divide-y divide-sand/30 ${isPrayerRoom ? 'opacity-90' : ''}`}>
                {comments.map((comment) => (
                  <CommentThread 
                    key={comment.id} 
                    comment={comment}
                    onReply={(parentId, content) => handleAddComment(parentId, content)}
                    onSendDM={onSendDM}
                  />
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-4 text-sm">
                Be the first to respond
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Create Post Dialog
const CreatePostDialog = ({ community, onPostCreated, celebrate }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [supportType, setSupportType] = useState('');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const token = localStorage.getItem('token');
  
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length + mediaFiles.length > 4) {
      toast.error('Maximum 4 media files allowed');
      return;
    }
    
    setUploading(true);
    const uploadedUrls = [];
    
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      
      try {
        const response = await axios.post(
          `${API_URL}/api/communities/upload-media`,
          formData,
          { 
            headers: { 
              Authorization: `Bearer ${token}`,
              'Content-Type': 'multipart/form-data'
            }
          }
        );
        uploadedUrls.push(response.data.url);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setMediaFiles([...mediaFiles, ...uploadedUrls]);
    setUploading(false);
  };
  
  const removeMedia = (index) => {
    setMediaFiles(mediaFiles.filter((_, i) => i !== index));
  };
  
  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error('Please write something to share');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(
        `${API_URL}/api/communities/${community.id}/posts`,
        {
          community_id: community.id,
          title: title.trim() || null,
          content: content.trim(),
          support_type: supportType || null,
          media_urls: mediaFiles,
          post_type: 'discussion'
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (celebrate) {
        celebrate('hearts', 'firstPost');
      }
      setTitle('');
      setContent('');
      setSupportType('');
      setMediaFiles([]);
      setOpen(false);
      onPostCreated();
    } catch (error) {
      toast.error('Failed to create post');
    } finally {
      setSubmitting(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-forest hover:bg-forest/90 gap-2" data-testid="create-post-btn">
          <Plus className="h-4 w-4" />
          Create Post
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-forest">Share with the Community</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 mt-4">
          {/* Posting Guidance */}
          <div className="bg-sand/30 rounded-lg p-4">
            <p className="text-sm text-forest/80 font-medium mb-2">
              What kind of support are you looking for?
            </p>
            <div className="grid grid-cols-2 gap-2">
              {SUPPORT_TYPES.map((type) => (
                <button
                  key={type.value}
                  onClick={() => setSupportType(supportType === type.value ? '' : type.value)}
                  className={`text-left p-2 rounded-lg border transition-colors ${
                    supportType === type.value 
                      ? 'border-terracotta bg-terracotta/10' 
                      : 'border-sand hover:border-forest/30'
                  }`}
                >
                  <span className="text-sm font-medium text-forest">{type.label}</span>
                  <span className="text-xs text-gray-500 block">{type.description}</span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Title (optional) */}
          <div>
            <Label className="text-sm text-gray-600">Title (optional)</Label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Give your post a title..."
              className="mt-1"
            />
          </div>
          
          {/* Content */}
          <div>
            <Label className="text-sm text-gray-600">Your message</Label>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Share your thoughts, questions, or experiences..."
              className="mt-1 min-h-[120px]"
              rows={5}
              data-testid="post-content-textarea"
            />
            <div className="mt-2 flex items-center gap-2">
              <VoiceInput 
                onTranscript={(text) => setContent(prev => prev ? `${prev} ${text}` : text)}
              />
              <span className="text-xs text-gray-400">or speak your message</span>
            </div>
          </div>
          
          {/* Media Upload */}
          <div>
            <Label className="text-sm text-gray-600">Add media (optional)</Label>
            <div className="mt-1">
              {mediaFiles.length > 0 && (
                <div className="grid grid-cols-4 gap-2 mb-2">
                  {mediaFiles.map((url, idx) => (
                    <div key={idx} className="relative aspect-square rounded-lg overflow-hidden bg-sand">
                      {url.includes('.mp4') || url.includes('.webm') || url.includes('.mov') ? (
                        <video src={`${API_URL}${url}`} className="w-full h-full object-cover" />
                      ) : (
                        <img src={`${API_URL}${url}`} alt="" className="w-full h-full object-cover" />
                      )}
                      <button
                        onClick={() => removeMedia(idx)}
                        className="absolute top-1 right-1 p-1 bg-black/50 rounded-full hover:bg-black/70"
                      >
                        <X className="h-3 w-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {mediaFiles.length < 4 && (
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed border-sand rounded-lg cursor-pointer hover:border-forest/30 transition-colors">
                  <input
                    type="file"
                    accept="image/*,video/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={uploading}
                  />
                  {uploading ? (
                    <span className="text-sm text-gray-500">Uploading...</span>
                  ) : (
                    <>
                      <ImageIcon className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-500">Add photos or videos</span>
                    </>
                  )}
                </label>
              )}
            </div>
          </div>
          
          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <Button variant="outline" onClick={() => setOpen(false)} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || !content.trim()}
              className="flex-1 bg-forest hover:bg-forest/90"
              data-testid="submit-post-btn"
            >
              {submitting ? 'Sharing...' : 'Share Post'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// DM Dialog
const DMDialog = ({ open, onOpenChange, recipientId, recipientName, communityId }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const token = localStorage.getItem('token');
  
  const handleSend = async () => {
    if (!message.trim()) return;
    
    setSending(true);
    try {
      await axios.post(
        `${API_URL}/api/messages`,
        { receiver_id: recipientId, content: message },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Message sent to ${recipientName}`);
      setMessage('');
      onOpenChange(false);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-forest">
            Message {recipientName}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Write your private message..."
            rows={4}
          />
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
              Cancel
            </Button>
            <Button 
              onClick={handleSend}
              disabled={sending || !message.trim()}
              className="flex-1 bg-forest hover:bg-forest/90"
            >
              {sending ? 'Sending...' : 'Send Message'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main Component
const CommunityDetailPage = () => {
  const { communityId } = useParams();
  const { grandCelebrate } = useCelebration();
  const [community, setCommunity] = useState(null);
  const [posts, setPosts] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('recent');
  const [dmTarget, setDmTarget] = useState(null);
  
  const token = localStorage.getItem('token');
  const isPrayerRoom = communityId === 'comm-ic-prayer';
  
  const fetchData = useCallback(async () => {
    try {
      const [postsRes, communityRes, membersRes] = await Promise.all([
        axios.get(`${API_URL}/api/communities/${communityId}/posts?sort_by=${sortBy}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/communities/${communityId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/api/communities/${communityId}/members`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: [] }))
      ]);
      
      // Handle both old and new response formats
      const postsData = postsRes.data.posts || postsRes.data;
      const communityData = postsRes.data.community || communityRes.data;
      
      setPosts(postsData);
      setCommunity(communityData);
      setMembers(membersRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load community');
    } finally {
      setLoading(false);
    }
  }, [communityId, sortBy, token]);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleReact = async (postId, reactionType) => {
    try {
      await axios.post(
        `${API_URL}/api/communities/${communityId}/posts/${postId}/reactions`,
        { reaction_type: reactionType },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
    } catch (error) {
      toast.error('Failed to react');
    }
  };
  
  const handleBookmark = async (postId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/communities/${communityId}/posts/${postId}/bookmark`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to bookmark');
    }
  };
  
  const handleSendDM = (userId, userName) => {
    setDmTarget({ id: userId, name: userName });
  };
  
  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8">
          <div className="animate-pulse">
            <div className="h-48 bg-sand rounded-xl mb-6" />
            <div className="h-8 bg-sand rounded w-1/3 mb-4" />
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-40 bg-sand rounded-xl" />
              ))}
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }
  
  if (!community) {
    return (
      <DashboardLayout>
        <div className="p-6 lg:p-8 text-center">
          <h1 className="font-heading text-2xl text-forest mb-4">Community Not Found</h1>
          <Link to="/communities">
            <Button className="btn-primary">Back to Communities</Button>
          </Link>
        </div>
      </DashboardLayout>
    );
  }
  
  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8" data-testid="community-detail-page">
        <Link to="/communities" className="inline-flex items-center gap-2 text-forest hover:text-terracotta mb-6 transition-colors">
          <ArrowLeft className="h-4 w-4" />
          Back to Communities
        </Link>
        
        {/* Community Header */}
        <div className="relative rounded-2xl overflow-hidden mb-8">
          <img 
            src={community.image_url} 
            alt={community.name}
            className="w-full h-48 md:h-64 object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-forest/90 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <span className="inline-block px-3 py-1 bg-white/90 text-forest text-sm rounded-full mb-3">
              {community.category}
            </span>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
              {community.name}
            </h1>
            <div className="flex items-center gap-4 text-white/80">
              <span className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {community.member_count || members.length} members
              </span>
            </div>
          </div>
        </div>
        
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* About & Guidelines */}
            <Card className="card-base">
              <CardContent className="p-6">
                <h2 className="font-heading text-xl font-semibold text-forest mb-3">About This Room</h2>
                <p className="text-gray-600 leading-relaxed mb-4">{community.description}</p>
                {community.guidelines && (
                  <div className="bg-sand/30 rounded-lg p-4">
                    <h3 className="text-sm font-medium text-forest mb-2">Guidelines</h3>
                    <p className="text-sm text-gray-600">{community.guidelines}</p>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Create Post & Sort */}
            <div className="flex items-center justify-between gap-4">
              <CreatePostDialog community={community} onPostCreated={fetchData} celebrate={grandCelebrate} />
              
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">Sort:</span>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="text-sm border border-sand rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-forest/20"
                  data-testid="sort-select"
                >
                  <option value="recent">Most Recent</option>
                  <option value="supported">Most Supported</option>
                </select>
              </div>
            </div>
            
            {/* Posts Feed */}
            {posts.length === 0 ? (
              <Card className="card-base">
                <CardContent className="p-12 text-center">
                  <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                  <h3 className="font-heading text-lg text-forest mb-2">Start the Conversation</h3>
                  <p className="text-gray-500 mb-4">Be the first to share something with this circle.</p>
                  <CreatePostDialog community={community} onPostCreated={fetchData} celebrate={grandCelebrate} />
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    community={community}
                    onReact={handleReact}
                    onBookmark={handleBookmark}
                    onComment={fetchData}
                    onSendDM={handleSendDM}
                    isPrayerRoom={isPrayerRoom}
                  />
                ))}
              </div>
            )}
          </div>
          
          {/* Sidebar */}
          <div className="space-y-6">
            {/* Room-Specific Tips */}
            {isPrayerRoom && (
              <Card className="card-base border-terracotta/30">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">🙏</span>
                    <h3 className="font-medium text-forest">Sacred Space</h3>
                  </div>
                  <p className="text-sm text-gray-600">
                    This is a space for faith-centered support. Respond with compassion, not advice unless asked.
                  </p>
                </CardContent>
              </Card>
            )}
            
            {/* Members */}
            <Card className="card-base">
              <CardContent className="p-6">
                <h3 className="font-heading text-lg font-semibold text-forest mb-4">
                  Members ({members.length})
                </h3>
                
                {members.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">Be the first to join!</p>
                ) : (
                  <div className="space-y-3">
                    {members.slice(0, 8).map((member) => (
                      <div key={member.id} className="flex items-center gap-3">
                        <Avatar className="h-9 w-9">
                          <AvatarImage src={member.profile_image} />
                          <AvatarFallback className="bg-forest text-white text-sm">
                            {member.full_name?.charAt(0)?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-forest text-sm truncate">{member.full_name}</p>
                        </div>
                        <button 
                          onClick={() => handleSendDM(member.id, member.full_name)}
                          className="p-2 hover:bg-sand/50 rounded-lg transition-colors"
                        >
                          <MessageSquare className="h-4 w-4 text-gray-400 hover:text-forest" />
                        </button>
                      </div>
                    ))}
                    
                    {members.length > 8 && (
                      <p className="text-center text-sm text-gray-500 pt-2">
                        +{members.length - 8} more members
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* DM Dialog */}
        <DMDialog
          open={!!dmTarget}
          onOpenChange={(open) => !open && setDmTarget(null)}
          recipientId={dmTarget?.id}
          recipientName={dmTarget?.name}
          communityId={communityId}
        />
      </div>
    </DashboardLayout>
  );
};

export default CommunityDetailPage;
